import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { mainContentClassName } from '../context/NavContext';
import { MealPlanConstraintsEditor } from './Profile';
import {
  constraintsWithoutUsageTracking,
  defaultMealPlanConstraints,
  normalizeMealPlanConstraints,
  summarizeConstraints
} from '../utils/mealPlanConstraints';

function GenerateMealPlan() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [mealTypes, setMealTypes] = useState(['dinner']);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState('');
  const [constraints, setConstraints] = useState(defaultMealPlanConstraints());
  const [constraintsCustomized, setConstraintsCustomized] = useState(false);
  const [showConstraints, setShowConstraints] = useState(false);

  useEffect(() => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    setStartDate(nextMonth.toISOString().split('T')[0]);
    setEndDate(endOfMonth.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (user?.mealPlanConstraints) {
      const normalized = normalizeMealPlanConstraints(user.mealPlanConstraints);
      setConstraints(normalized);
      setMealTypes(normalized.defaultMealTypes);
    }
  }, [user]);

  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const response = await axios.get(`/api/constraint-profiles`);
        setPresets(response.data || []);
      } catch (fetchError) {
        console.error('Error fetching constraint presets:', fetchError);
      }
    };
    fetchPresets();
  }, []);

  useEffect(() => {
    if (!presetId) {
      if (user?.mealPlanConstraints) {
        setConstraints(normalizeMealPlanConstraints(user.mealPlanConstraints));
        setConstraintsCustomized(false);
      }
      return;
    }
    const preset = presets.find((p) => p._id === presetId);
    if (preset?.mealPlanConstraints) {
      setConstraints(normalizeMealPlanConstraints(preset.mealPlanConstraints));
      setConstraintsCustomized(false);
      if (preset.mealPlanConstraints.defaultMealTypes?.length) {
        setMealTypes(preset.mealPlanConstraints.defaultMealTypes);
      }
    }
  }, [presetId, presets, user]);

  const buildRequestPayload = () => {
    const payload = {
      startDate,
      endDate,
      mealTypes,
      overwriteExisting
    };
    if (presetId) {
      payload.presetId = presetId;
    }
    if (constraintsCustomized) {
      payload.constraints = constraintsWithoutUsageTracking(constraints);
    }
    return payload;
  };

  const handlePreview = async () => {
    if (!startDate || !endDate) {
      setError('Please fill in all required fields');
      return;
    }

    if (generateLoading) return;

    setError('');
    setPreviewLoading(true);
    setPreview(null);

    try {
      const response = await axios.post(`/api/meal-plan-generation/preview`, buildRequestPayload());
      if (response.data?.meals) {
        response.data.meals = response.data.meals.map((meal) => ({
          ...meal,
          date: meal.date ? (typeof meal.date === 'string' ? meal.date : new Date(meal.date).toISOString()) : null
        }));
      }
      setPreview(response.data);

      if (response.data?.errors && response.data.errors.length > 0) {
        setError(response.data.errors.join('. '));
      } else if (response.data?.warnings && response.data.warnings.length > 0) {
        const errorWarning = response.data.warnings.find((w) => w.type === 'error');
        if (errorWarning) {
          setError(errorWarning.message || 'Failed to generate meal plan');
        }
      }
    } catch (previewError) {
      console.error('Preview error:', previewError);
      let errorMessage = previewError.response?.data?.error || previewError.response?.data?.message;
      if (!errorMessage) {
        if (previewError.code === 'ERR_NETWORK') {
          errorMessage = 'Cannot reach the server. Make sure the backend is running (npm run dev).';
        } else {
          errorMessage = 'Failed to generate preview';
        }
      }
      setError(errorMessage);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setError('Please fill in all required fields');
      return;
    }

    if (!window.confirm('This will generate and save the meal plan. Continue?')) {
      return;
    }

    if (previewLoading) return;

    setError('');
    setGenerateLoading(true);

    try {
      const response = await axios.post(`/api/meal-plan-generation/generate`, buildRequestPayload());

      alert(`Successfully generated ${response.data.savedCount} meals!`);
      navigate('/calendar');
    } catch (generateError) {
      setError(generateError.response?.data?.error || 'Failed to generate meal plan');
    } finally {
      setGenerateLoading(false);
    }
  };

  const constraintSummary = summarizeConstraints(constraints);

  return (
    <>
      <div
        className="fixed inset-0"
        style={{
          backgroundImage: 'url(/img/form-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1,
          minHeight: '100vh',
          height: '100%'
        }}
      />
      <Navigation />
      <div className={mainContentClassName}>
        <div className="max-w-6xl mx-auto">
          <div className="bg-white/60 backdrop-blur-sm rounded-lg shadow-lg p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">Generate Meal Plan</h1>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p className="font-semibold mb-1">Error:</p>
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <p>
                Pick a saved preset or use your profile defaults, then customize constraints for this run.
                Recipe usage tracking always comes from your{' '}
                <Link to="/profile" className="text-amber-600 hover:underline font-semibold">Profile</Link>.
                {' '}
                <Link to="/constraint-profiles" className="text-amber-600 hover:underline font-semibold">
                  Manage presets
                </Link>
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Constraint preset</label>
                <select
                  value={presetId}
                  onChange={(e) => setPresetId(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                >
                  <option value="">Profile defaults</option>
                  {presets.map((preset) => (
                    <option key={preset._id} value={preset._id}>
                      {preset.name}{preset.isDefault ? ' (default preset)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meal Types (this run)</label>
                <div className="flex flex-wrap gap-4">
                  {['breakfast', 'lunch', 'dinner', 'snack'].map((type) => (
                    <label key={type} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={mealTypes.includes(type)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMealTypes([...mealTypes, type]);
                          } else {
                            setMealTypes(mealTypes.filter((t) => t !== type));
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="capitalize">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowConstraints((open) => !open)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white/80 hover:bg-white text-left"
                >
                  <span className="font-semibold text-gray-800">Customize constraints for this run</span>
                  <span className="text-sm text-gray-500">
                    {constraintSummary.rules} rules · {showConstraints ? 'Hide' : 'Show'}
                    {constraintsCustomized ? ' · customized' : ''}
                  </span>
                </button>
                {showConstraints && (
                  <div className="p-4 bg-white/90 border-t border-gray-200">
                    <MealPlanConstraintsEditor
                      embedded
                      value={constraints}
                      onChange={(next) => {
                        setConstraintsCustomized(true);
                        setConstraints(next);
                      }}
                      showDefaultMealTypes={false}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="overwrite"
                  checked={overwriteExisting}
                  onChange={(e) => setOverwriteExisting(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="overwrite" className="text-sm font-medium text-gray-700">
                  Overwrite existing meals in date range
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handlePreview}
                  disabled={previewLoading || !startDate || !endDate}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {previewLoading ? 'Generating...' : 'Preview'}
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={generateLoading || !startDate || !endDate}
                  className="px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {generateLoading ? 'Generating...' : 'Generate & Save'}
                </button>
              </div>
            </div>

            {preview && <PreviewResults preview={preview} />}
          </div>
        </div>
      </div>
    </>
  );
}

function PreviewResults({ preview }) {
  const [groupedMeals, setGroupedMeals] = useState({});

  useEffect(() => {
    const grouped = {};
    if (preview?.meals && Array.isArray(preview.meals)) {
      preview.meals.forEach((meal) => {
        if (meal?.date) {
          try {
            const date = new Date(meal.date);
            if (!isNaN(date.getTime())) {
              const dateStr = date.toLocaleDateString();
              if (!grouped[dateStr]) {
                grouped[dateStr] = [];
              }
              grouped[dateStr].push(meal);
            }
          } catch (e) {
            console.error('Error parsing date:', meal.date, e);
          }
        }
      });
    }
    setGroupedMeals(grouped);
  }, [preview]);

  return (
    <div className="mt-6 border-t pt-6">
      <h2 className="text-xl font-semibold mb-4">Preview</h2>

      <div className="mb-4 p-4 bg-white/80 rounded-lg">
        <p className="font-semibold">Status:</p>
        <ul className="text-sm text-gray-600 space-y-1 mt-2">
          <li>Total meals: {preview?.status?.totalMeals ?? 0}</li>
          <li>Warnings: {preview?.status?.warnings ?? 0}</li>
          {(preview?.status?.errors ?? 0) > 0 && <li>Errors: {preview.status.errors}</li>}
        </ul>
      </div>

      {preview?.warnings && Array.isArray(preview.warnings) && preview.warnings.length > 0 && (
        <div className="mb-4 p-4 bg-yellow-100 border border-yellow-400 rounded-lg">
          <p className="font-semibold text-yellow-800 mb-2">Warnings:</p>
          <ul className="text-sm text-yellow-700 space-y-1">
            {preview.warnings.map((warning, idx) => (
              <li key={idx}>• {warning?.message || JSON.stringify(warning)}</li>
            ))}
          </ul>
        </div>
      )}

      {preview?.pantrySuggestions && preview.pantrySuggestions.length > 0 && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="font-semibold text-blue-900 mb-2">Freezer / pantry notes:</p>
          <ul className="text-sm text-blue-800 space-y-1">
            {preview.pantrySuggestions.map((item, idx) => (
              <li key={idx}>• {item.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="max-h-[600px] overflow-y-auto space-y-4">
        {Object.entries(groupedMeals).map(([date, meals]) => (
          <div key={date} className="bg-white/80 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">{date}</h3>
            <div className="space-y-1">
              {meals.map((meal, idx) => (
                <div key={idx} className="text-sm text-gray-600 flex items-center gap-2 flex-wrap">
                  <span className="capitalize font-medium">{meal?.mealType || 'Unknown'}:</span>
                  <span>{meal?.recipe?.title || 'Unknown Recipe'}</span>
                  {(meal?.isLeftover || meal?.pantryItemId) && (
                    <span className="text-xs bg-gray-100 text-gray-700 border border-dashed border-gray-500 px-2 py-0.5 rounded">
                      leftovers
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GenerateMealPlan;
